/**
 * Domain types for the portal's non-chat surfaces (home, operators, regions, account, reports).
 *
 * They describe what the CMS / tRPC layer will eventually serve, and are deliberately shaped so
 * the fixtures in this folder can be swapped for real queries without touching the pages: every
 * figure is a plain number in base units (pence-free currency units, not "bn"), every date an ISO
 * `YYYY-MM-DD` string, every cross-reference a slug. Formatting is the page's job
 * (`~portal/lib/format`), never the data's.
 *
 * Conventions:
 * - Ratios (`share`, `growth`, `margin`) are fractions — `0.124` is 12.4%.
 * - Percentage-point changes (`shareChangePp`) are fractions too — `0.012` is +1.2 pp.
 * - A section's data is optional exactly where the page hides that section when it is absent
 *   (client decision: "sections hide when their data is absent"), so the type says which
 *   sections can vanish.
 */

/** @desc ISO 4217 codes for every currency a fixture reports in. */
export type CurrencyCode =
  'GBP' | 'EUR' | 'USD' | 'SEK' | 'DKK' | 'BRL' | 'COP' | 'MXN' | 'CAD' | 'AUD' | 'ZAR';

/** @desc An amount in whole units of `currency` (e.g. `6_600_000_000` GBP is £6.6bn). */
export type Money = { value: number; currency: CurrencyCode };

/** @desc An ISO calendar date, `YYYY-MM-DD`. */
export type IsoDate = string;

/** @desc An ISO calendar month, `YYYY-MM`. */
export type IsoMonth = string;

// ---------------------------------------------------------------------------------------------
// Shared building blocks
// ---------------------------------------------------------------------------------------------

/** @desc A point of a single-series chart. `forecast` marks an estimate (drawn lighter, "e"). */
export type SeriesPoint = { label: string; value: number; forecast?: boolean };

/** @desc What a cross-link points at: an operator group's page or a region's page. */
export type SubjectRef = { kind: 'operator' | 'region'; slug: string; name: string };

export type NewsItem = {
  id: string;
  headline: string;
  source: string;
  date: IsoDate;
  /** @desc One or two sentences, for cards that have room. */
  summary?: string;
  /** @desc Operator groups the story is about (slugs), for the company news flow. */
  groupSlugs?: string[];
  /** @desc Regions the story is about (slugs), for the market news flow. */
  regionSlugs?: string[];
};

export type DownloadKind =
  'financial-model' | 'company-update' | 'call-notes' | 'regulatory-summary' | 'market-report';

/** @desc A downloadable document. Nothing is wired yet — every download is illustrative. */
export type DownloadItem = {
  id: string;
  kind: DownloadKind;
  title: string;
  description: string;
  format: 'xlsx' | 'pdf';
  sizeBytes: number;
  updated: IsoDate;
};

// ---------------------------------------------------------------------------------------------
// Operators
// ---------------------------------------------------------------------------------------------

export type EntityType = 'operator-b2c' | 'supplier-b2b' | 'national-operator';

export const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  'operator-b2c': 'Operator (B2C)',
  'supplier-b2b': 'Supplier (B2B)',
  'national-operator': 'National operator',
};

/**
 * @desc A consumer-facing brand. Brands have no page of their own (client decision: company pages
 * are per group), so every brand link resolves to `groupSlug`. Which brands a group has, and their
 * identity, come from the Sanity `operator` documents; the figures from the fixture, by slug or
 * name (openspec change `operator-brands-in-sanity`).
 */
export type Brand = {
  slug: string;
  name: string;
  groupSlug: string;
  type: EntityType;
  /** @desc One or two sentences on what the brand is — the directory card's two lines (D-3). */
  description: string;
  /** @desc Display label of the brand's home market, e.g. `UK`, `US – New Jersey`. */
  hqMarket: string;
  /** @desc The home market's region slug, when the portal has a page for it. */
  hqRegionSlug?: string;
  /**
   * @desc Zero for a B2B supplier, which earns revenue rather than GGR — render it as `—`. Absent
   * for an identity-only brand.
   */
  onlineGgr?: Money;
  /** @desc The same figure in USD, so a GGR range filter can compare brands across currencies. */
  onlineGgrUsd?: number;
  yoyGrowth?: number;
  /** @desc Region slugs the brand is licensed in. */
  operatingRegions: string[];
  /** @desc Set when the brand is a joint venture (BetMGM is Entain's 50/50 JV with MGM). */
  jointVenture?: string;
  /** @desc The Sanity logo's CDN URL (no size parameters); absent → the shipped asset or initial. */
  logoUrl?: string;
  /** @desc `true` for a Sanity brand the fixture has no entry for: identity only, no figures. */
  identityOnly?: true;
};

/** @desc The headline financial strip of a company page. */
export type GroupFinancials = {
  /** @desc The period the strip reports, e.g. `FY2025`. */
  period: string;
  currency: CurrencyCode;
  revenue: number;
  adjustedEbitda: number;
  /** @desc Adjusted EBITDA ÷ revenue. */
  ebitdaMargin: number;
  operatingProfit: number;
  onlineGgr: number;
  /** @desc Same figure in USD, for cross-currency sorting and filtering. */
  onlineGgrUsd: number;
  /** @desc Monthly active users (average over the period). */
  mau: number;
  /** @desc Revenue growth year on year. */
  yoyGrowth: number;
};

/** @desc One slice of a group's revenue by jurisdiction. Slices sum to 1. */
export type RegionalSplitSlice = { label: string; regionSlug?: string; share: number };

/** @desc A row of the "H2 view of market share" table on a company page. */
export type MarketShareRow = {
  regionSlug: string;
  market: string;
  estGgrLocal: Money;
  estGgrUsd: number;
  /** @desc This market's share of the company's own online GGR. */
  shareOfCompanyGgr: number;
  /** @desc 1 = market leader. */
  marketPosition: number;
  marketShare: number;
  /** @desc Year-on-year change in market share, in percentage points as a fraction. */
  shareChangePp: number;
};

/** @desc An H2 analyst note on the company's markets. */
export type MarketUpdate = {
  id: string;
  title: string;
  date: IsoDate;
  summary: string;
  author: string;
};

/** @desc AI-summarised earnings call, one per quarter. */
export type CallSummary = { id: string; quarter: string; date: IsoDate; bullets: string[] };

export type AcquisitionRef = { slug?: string; name: string; year: number };

export type ListingStatus = 'listed' | 'delisted';

/**
 * @desc One stock-exchange line of a group (workbook D15/D18). Exchange tickers were verified
 * against the exchanges on 13 Oct 2026; the TradingView and Google Finance spellings follow each
 * vendor's own convention (`OMXSTO:BETS_B`, `BETS-B:STO`) and are what the chips and the chart
 * widget are given. No price is stored anywhere — the portal only links out or embeds.
 */
export type ShareListing = {
  /** @desc ISO 10383 market identifier code, e.g. `XNYS`. */
  mic: string;
  /** @desc The exchange as a reader names it: `NYSE`, `LSE`, `Euronext Paris`. */
  exchange: string;
  symbol: string;
  isin?: string;
  /** @desc ISO 4217, or `GBX` for a London line quoted in pence. */
  currency: CurrencyCode | 'GBX';
  /** @desc The line the page charts and leads with; exactly one per group with any listing. */
  primary: boolean;
  status: ListingStatus;
  /** @desc Last trading day, when `status` is `delisted`. */
  until?: IsoDate;
  tradingViewSymbol: string;
  googleFinanceSymbol: string;
  /** @desc The exchange's own quote page, where it has a stable URL. */
  exchangeUrl?: string;
};

export type OperatorGroup = {
  slug: string;
  name: string;
  classification: EntityType;
  /** @desc Headquarters as a reader says it, e.g. `Dublin, Ireland`. */
  hq: string;
  /** @desc The HQ market as the listing's "HQ market" column and location filter read it. */
  hqMarket: string;
  hqRegionSlug?: string;
  /** @desc Absent when the directory document does not say. */
  founded?: number;
  founder: string;
  /** @desc Stock-exchange lines, primary first; empty for a private company (bet365). */
  listings: ShareListing[];
  sectors: string[];
  /** @desc One line on where the group operates. */
  footprint: string;
  /** @desc One line on mergers and acquisitions. */
  maSummary: string;
  /** @desc 2–3 sentences of AI commentary for the head of the page. */
  commentary: string;
  /** @desc `sold` renders the frozen holding page (client decision: Kindred → FDJ United). */
  status: 'active' | 'sold';
  /** @desc Present when `status` is `sold`: who bought it, and when. */
  acquiredBy?: AcquisitionRef;
  /** @desc Companies this group bought, newest first — the acquirer side of a sold page's link. */
  acquisitions: AcquisitionRef[];
  brands: Brand[];
  /** @desc Region slugs the group operates in, for the "Operating regions" chip grid. */
  operatingRegions: string[];
  financials?: GroupFinancials;
  regionalSplit?: RegionalSplitSlice[];
  marketShare?: MarketShareRow[];
  news: NewsItem[];
  marketUpdates: MarketUpdate[];
  callSummaries: CallSummary[];
  downloads: DownloadItem[];
  /** @desc The company's one-line description from its Sanity document (not illustrative). */
  description?: string;
  /** @desc The Sanity logo's CDN URL (no size parameters); absent → the initial tile. */
  logoUrl?: string;
  /**
   * @desc `true` for a directory company the illustrative fixture has no entry for: identity only,
   * so the page shows no figures, brands or badged sections (openspec `operator-groups-in-sanity`).
   */
  identityOnly?: true;
};

// ---------------------------------------------------------------------------------------------
// Regions
// ---------------------------------------------------------------------------------------------

export type RegionKind = 'market' | 'continent' | 'rollup';

export type RegulatoryStatus = 'regulated' | 'partial' | 'unregulated';

export const REGULATORY_STATUS_LABELS: Record<RegulatoryStatus, string> = {
  regulated: 'Regulated',
  partial: 'Partially regulated',
  unregulated: 'Unregulated',
};

export type RegionKpis = {
  /** @desc The year the KPIs describe (the latest full year, actuals). */
  year: number;
  onlineGgrLocal: Money;
  onlineGgrUsd: number;
  /** @desc Next year's forecast online GGR, USD. */
  forecastGgrUsd: number;
  forecastYear: number;
  /** @desc Online GGR per adult, USD. */
  ggrPerAdultUsd: number;
  adultPopulation: number;
  yoyGrowth: number;
  /** @desc Active online players. */
  activePlayers: number;
  /** @desc Online GGR per active player, USD. */
  ggrPerPlayerUsd: number;
};

export type RegionMacro = {
  /** @desc Online GGR as a share of all GGR (online + land). */
  onlineShareOfTotal: number;
  gdpUsd: number;
  inflation: number;
  gdpGrowth: number;
};

export type RegulatorySnapshot = {
  regulator: string;
  firstRegulated: number;
  licenceTypes: string[];
  /** @desc Tax as quoted, e.g. `21% of GGR`. */
  taxOnline: string;
  taxLand: string;
  /** @desc One line on what is changing. */
  outlook: string;
};

export type AnnualGgrView = 'by-product' | 'online-vs-land' | 'onshore-vs-offshore';

export const ANNUAL_GGR_VIEW_LABELS: Record<AnnualGgrView, string> = {
  'by-product': 'By product',
  'online-vs-land': 'Online vs land',
  'onshore-vs-offshore': 'Onshore vs offshore',
};

/**
 * @desc One annual GGR series set (a tab of the region chart): several segments over the same
 * years, each a column series in USD. Points from `forecastFrom` on are estimates.
 */
export type AnnualGgrSet = {
  forecastFrom: number;
  segments: Array<{ key: string; label: string; points: SeriesPoint[] }>;
};

/** @desc An operator's share of a market. `groupSlug` links it when the portal covers the group. */
export type OperatorShare = { name: string; groupSlug?: string; share: number };

export type MonthlyTrackingPoint = {
  month: IsoMonth;
  ggrUsd: number;
  /** @desc Share of gambling web traffic going to licensed sites. */
  licensedTrafficShare: number;
};

export type SurveyData = {
  fieldwork: string;
  sampleSize: number;
  brandAwareness: OperatorShare[];
  choiceOfOperator: OperatorShare[];
  accountsPerPlayer: number;
};

export type Region = {
  slug: string;
  name: string;
  kind: RegionKind;
  /** @desc Short code for the initial tile: ISO country code, or a state/province code. */
  code: string;
  /** @desc The continent a market belongs to; absent on continents. */
  continentSlug?: string;
  regulatoryStatus: RegulatoryStatus;
  currency: CurrencyCode;
  kpis: RegionKpis;
  macro: RegionMacro;
  /** @desc Markets only; continents and roll-ups show child markets instead. */
  regulatory?: RegulatorySnapshot;
  /** @desc AI market overview paragraph. */
  overview: string;
  annualGgr: Record<AnnualGgrView, AnnualGgrSet>;
  marketShare: OperatorShare[];
  monthlyTracking: MonthlyTrackingPoint[];
  /** @desc Group slugs with a licensed presence. */
  operatorsPresent: string[];
  /** @desc Licensed operators in the market (all of them, not just the groups the portal covers). */
  licensedOperatorCount: number;
  news: NewsItem[];
  survey?: SurveyData;
  /** @desc Continents: slugs of their markets. */
  childMarkets?: string[];
  /** @desc Roll-ups: the untracked countries they aggregate, by name. */
  rollupOf?: string[];
  downloads: DownloadItem[];
};

// ---------------------------------------------------------------------------------------------
// Home
// ---------------------------------------------------------------------------------------------

export type AnalystNote = {
  id: string;
  title: string;
  author: { name: string; role: string };
  date: IsoDate;
  excerpt: string;
  subject: SubjectRef;
};

export type UpdateKind = 'analyst-note' | 'financial-model' | 'regulatory' | 'market-share';

export type PlatformUpdate = {
  id: string;
  kind: UpdateKind;
  title: string;
  date: IsoDate;
  subject: SubjectRef;
};

export type TrackedPage = SubjectRef & { followedAt: IsoDate; lastUpdated: IsoDate };

export type ExploreCard = {
  key: 'agent' | 'operators' | 'regions' | 'reports';
  title: string;
  description: string;
};

export type HomeData = {
  latestNote: AnalystNote;
  recentUpdates: PlatformUpdate[];
  news: NewsItem[];
  tracked: TrackedPage[];
  explore: ExploreCard[];
  workspace: WorkspaceMember[];
};

// ---------------------------------------------------------------------------------------------
// Account
// ---------------------------------------------------------------------------------------------

export type WorkspaceRole = 'admin' | 'member';

export type WorkspaceMember = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: string;
  role: WorkspaceRole;
  lastActive: IsoDate;
};

export type AccountProfile = {
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: string;
  timeZone: string;
};

export type Company = {
  name: string;
  website: string;
  sector: string;
  size: string;
  address: string[];
  members: WorkspaceMember[];
};

export type PlanId = 'free' | 'plus' | 'pro';

/**
 * @desc A plan as the Figma plan cards draw it (`295:54048`, decision D-14): the Figma price,
 * seats and feature content as illustrative placeholders behind the badge.
 */
export type Plan = {
  id: PlanId;
  name: string;
  /** @desc The price figure alone (`£99`). No prices are agreed: Figma's, as placeholders. */
  price: string;
  /** @desc The cadence under the price (`per month`). */
  period: string;
  /** @desc The seats line (`Up to 5 seats`). */
  seats: string;
  /** @desc The card's call to action, as Figma words it (`Upgrade to H2GC Plus`). */
  cta: string;
  tagline: string;
  /** @desc The tier this one adds to ("Everything in Plus, and…"). */
  includes?: PlanId;
  features: string[];
};

export type Account = {
  profile: AccountProfile;
  company: Company;
  currentPlanId: PlanId;
  /** @desc Options for the profile and company selects. */
  options: { timeZones: string[]; sectors: string[]; sizes: string[] };
};

/** @desc The fixture hooks' return shape — a tRPC query's, so the later swap is mechanical. */
export type FixtureQuery<T> = { data: T; isLoading: false };

// ---------------------------------------------------------------------------------------------
// UI round 2 — Lane F additions (Home, Account, Reports). Appended, not merged into the sections
// above: `types.ts` is edited by three lanes at once.
// ---------------------------------------------------------------------------------------------

/**
 * @desc A live hook's return shape: the three states a page must render — pending (`isLoading`),
 * failed (`error`, shown inline, never silently replaced by a fixture) and loaded (`data`).
 */
export type LiveQuery<T> = {
  data: T | undefined;
  isLoading: boolean;
  error: { message: string } | null;
};
